import {
  addDays,
  getWeek,
  isPast,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import Cookies from "js-cookie";
import { combineDateAndTime, roundUpToNearestHalfHour } from "@/utils/date";
import {
  CALENDAR_VIEW_KEY,
  CalendarViews,
} from "../appointments/useCalendarTypeView";
import useMedspaTimezone from "../common/useMedspaTimezone";
import { useCalendarClickDate } from "./useCalendarClickDate";
import { getCurrentSessionDate } from "./useCurrentSessionDate";
import { useFormattedCurrentSessionDate } from "./useFormattedCurrentSessionDate";

type CalendarView = `${CalendarViews}`;

/**
 * @deprecated use useGetCreateAppointmentDefaultDate instead
 * This hook returns static data and is not updated when session date change or current time changes.
 * It was used in components that were mounted on demand which worked fine, but for components that
 * are mounted constantly (eg. MultiProviderAppointmentDrawer) it causes issues.
 */
export const useCreateAppointmentDefaultDate = () => {
  const { utcToMedspaZonedTime } = useMedspaTimezone();

  const sessionDate = utcToMedspaZonedTime(
    roundUpToNearestHalfHour(useFormattedCurrentSessionDate())
  );
  const today = utcToMedspaZonedTime(roundUpToNearestHalfHour(new Date()));

  const queryDate = useCalendarClickDate();

  const calendarView = Cookies.get(CALENDAR_VIEW_KEY) as CalendarView;

  return getCreateAppointmentDefaultDate({
    calendarView,
    queryDate,
    today,
    sessionDate,
    utcToMedspaZonedTime,
  });
};

export const getCreateAppointmentDefaultDate = ({
  calendarView,
  queryDate,
  today,
  sessionDate,
  utcToMedspaZonedTime,
}: {
  calendarView: CalendarView;
  // date from query params
  queryDate?: Date | null;
  // today's date rounded up to nearest half hour
  today: Date;
  // date from session storage rounded up to nearest half hour
  sessionDate: Date;
  utcToMedspaZonedTime: (date: string | number | Date) => Date;
}) => {
  switch (calendarView) {
    case CalendarViews.LIST:
      return today;
    case CalendarViews.DAY:
      return queryDate ?? sessionDate;

    // If we are in the today's current week or month, we use today.
    // For dates in the past, we use today
    case CalendarViews.WEEK: {
      if (queryDate) return queryDate;

      const selectedDateWeek = getWeek(sessionDate, {
        weekStartsOn: 0,
      });
      const todayWeek = getWeek(today, { weekStartsOn: 0 });

      if (selectedDateWeek === todayWeek) return today;

      const startOfWeekSelected = startOfWeek(sessionDate, {
        weekStartsOn: 0,
      }).setHours(today.getHours(), today.getMinutes());

      if (isPast(startOfWeekSelected)) return today;

      // Return Monday, not Sunday
      return utcToMedspaZonedTime(addDays(startOfWeekSelected, 1));
    }
    case CalendarViews.MONTH: {
      if (isSameMonth(sessionDate, today)) return today;

      // If we are in the future, we use the first day from the month
      const startOfMonthSelected = startOfMonth(sessionDate).setHours(
        today.getHours(),
        today.getMinutes()
      );
      if (isPast(startOfMonthSelected)) return today;

      return utcToMedspaZonedTime(startOfMonthSelected);
    }
    default:
      return today;
  }
};

const getFormattedCurrentSessionDate = () => {
  const currentSessionDateStr = getCurrentSessionDate();
  if (!currentSessionDateStr) {
    return null;
  }

  const currentSessionDate = new Date(currentSessionDateStr);
  const currentTime = new Date();
  const currentSessionDateWithTime = combineDateAndTime(
    currentSessionDate,
    currentTime
  );

  return currentSessionDateWithTime;
};

/**
 * Returns a function that returns the default date for booking the appointment.
 * Use it in components that are mounted constantly (eg. MultiProviderAppointmentDrawer)
 * to get the default date for booking the appointment on demand.
 */
export const useGetCreateAppointmentDefaultDate = () => {
  const { utcToMedspaZonedTime } = useMedspaTimezone();

  const queryDate = useCalendarClickDate();

  return () => {
    const today = utcToMedspaZonedTime(roundUpToNearestHalfHour(new Date()));
    const currentSessionDate = getFormattedCurrentSessionDate();
    const calendarSessionDate = currentSessionDate
      ? utcToMedspaZonedTime(roundUpToNearestHalfHour(currentSessionDate))
      : today;
    const calendarView = Cookies.get(CALENDAR_VIEW_KEY) as CalendarView;

    return getCreateAppointmentDefaultDate({
      calendarView,
      queryDate,
      today,
      sessionDate: calendarSessionDate,
      utcToMedspaZonedTime,
    });
  };
};
